// The ARTEMIS tab's hub (WEB-7168, Option B): a rail of four sections (Chats, Sessions, Alerts, Skills),
// each with its own list column. A session isn't a different kind of conversation: it's one whose
// scratchpad is on (Context.Scratchpad). An "ask" is a direct conversation with it off; a session is
// "shared" once a second member joins (Kind group); the alerts conversation (Kind alerts) is Alerts.
// Nothing is converted or copied: Open as session turns the scratchpad on for the same conversation.

import { isAlertsChat } from './alerts'

export const SECTIONS = ['chats', 'sessions', 'alerts', 'skills']

// The conversations this user opened as a session in this browser. The server says so in ScratchpadOn
// (API-3909) once a message with Context.Scratchpad has been sent; until then, and until API-3909 is
// built, this list does.
const SESSIONS_KEY = 'artemis.sessions'
const MAX_KEPT = 200

export const readOpenedSessions = () => {
  try {
    const list = JSON.parse(window.localStorage.getItem(SESSIONS_KEY) || '[]')
    return new Set(Array.isArray(list) ? list.map(String) : [])
  } catch (e) {
    return new Set()
  }
}

// → the new set, also written to this browser's storage.
export const rememberSession = (opened, id) => {
  if (!id || opened.has(String(id))) return opened
  const next = new Set([...opened, String(id)])
  try { window.localStorage.setItem(SESSIONS_KEY, JSON.stringify([...next].slice(-MAX_KEPT))) } catch (e) { /* no storage */ }
  return next
}

// Whether a list row is a session: shared (Kind group, where the server always has the scratchpad on),
// the server's ScratchpadOn, or opened as one here.
export const isSession = (row, opened = new Set()) => !!row && !isAlertsChat(row)
  && (row.kind === 'group' || row.scratchpadOn === true || opened.has(String(row.id)))

export const isShared = row => !!row && row.kind === 'group'

// Which section a conversation belongs in.
export const sectionFor = (row, opened) => (isAlertsChat(row) ? 'alerts' : isSession(row, opened) ? 'sessions' : 'chats')

// The Chats list: direct conversations with the scratchpad off.
export const chatRows = (rows, opened) => rows.filter(r => !isAlertsChat(r) && !isSession(r, opened))

// The Sessions list: "Shared with partners" above "Just me", each newest first as the server sent them.
export const sessionGroups = (rows, opened) => {
  const sessions = rows.filter(r => isSession(r, opened))
  return [
    { label: 'Shared with partners', chats: sessions.filter(isShared) },
    { label: 'Just me', chats: sessions.filter(r => !isShared(r)) }
  ].filter(g => g.chats.length > 0)
}

// API-3909's list filters, where the server has them: its rows then carry ScratchpadOn.
export const hasListFilters = rows => (rows || []).some(r => typeof r.scratchpadOn === 'boolean')
// Sessions: ?scratchpad=true. Chats: ?kind=direct (no group, no alerts), less the sessions among them.
export const listFilter = section => (section === 'sessions' ? { scratchpad: true } : { kind: 'direct' })

// A filtered page's rows for the list: Sessions also has the conversations opened as a session here that the
// server doesn't count yet (until their next message), from rows; Chats drops every session.
export const filteredRows = (section, listed, rows, opened = new Set()) => (section === 'sessions'
  ? [...listed, ...rows.filter(r => !isAlertsChat(r) && opened.has(String(r.id)) && !listed.some(l => l.id === r.id))]
  : listed.filter(r => !isSession(r, opened)))

// "4 drafts, 1 sent" from a session row's ScratchCount (every item) and ScratchSentCount (API-3909);
// null until the server sends them.
export const scratchLabel = row => {
  if (!row || !Number.isFinite(row.scratchCount)) return null
  const sent = Number.isFinite(row.scratchSentCount) ? row.scratchSentCount : 0
  const drafts = Math.max(0, row.scratchCount - sent)
  const parts = [
    (drafts > 0 || sent === 0) && `${drafts} ${drafts === 1 ? 'draft' : 'drafts'}`,
    sent > 0 && `${sent} sent`
  ].filter(Boolean)
  return parts.join(', ')
}

// The rail's and the ARTEMIS tab's counts: new alerts (the alerts conversation's UnreadCount) and
// mentions of this user across sessions (the sum of UnreadMentions).
export const hubBadges = rows => ({
  alerts: rows.filter(isAlertsChat).reduce((n, r) => n + (r.unread || 0), 0),
  mentions: rows.reduce((n, r) => n + (isAlertsChat(r) ? 0 : r.unreadMentions || 0), 0)
})

// The ARTEMIS tab's one badge: new alerts win; an @mention shows as @N when there are none.
// → { text, kind: 'alerts' | 'mention', label } or null.
export const tabBadge = badges => {
  if (!badges) return null
  if (badges.alerts > 0) return { text: String(badges.alerts), kind: 'alerts', label: `${badges.alerts} new ${badges.alerts === 1 ? 'alert' : 'alerts'}` }
  if (badges.mentions > 0) return { text: `@${badges.mentions}`, kind: 'mention', label: `${badges.mentions} ${badges.mentions === 1 ? 'mention' : 'mentions'} of you` }
  return null
}

// The ARTEMIS tab's counts while the hub isn't open: the alerts conversation's UnreadCount, and the bell's
// unread mentions only when there are no new alerts, since alerts win the badge.
// api: { inbox, alerts, conversation, mentions }. inbox is GET Conversations?kind=alerts&limit=1 (API-3909).
// A server without the filter ignores it and sends its newest conversation, without ScratchpadOn: then,
// for the rest of the page, the inbox's id comes from GET Alerts (kept in cache) and its row from
// GET Conversations/{id}.
export const lookUpBadges = async (api, cache) => {
  const gone = err => !!err && (err.status === 404 || err.code === 'http_404')
  if (cache.kindFilter !== false) {
    const items = ((await api.inbox()) || {}).Items || []
    if (items.length === 0 || typeof items[0].ScratchpadOn === 'boolean') {
      cache.kindFilter = true
      const inbox = items.find(c => c.Kind === 'alerts')
      const alerts = (inbox && Number(inbox.UnreadCount)) || 0
      const mentions = alerts > 0 ? 0 : (((await api.mentions()) || {}).Items || []).length
      return { alerts, mentions }
    }
    cache.kindFilter = false
  }
  if (!cache.alertsId) {
    const page = await api.alerts().catch(err => (gone(err) ? null : Promise.reject(err)))
    cache.alertsId = (page && page.AlertsConversationId) || null
  }
  let alerts = 0
  if (cache.alertsId) {
    const row = await api.conversation(cache.alertsId).catch(err => {
      if (!gone(err)) throw err
      cache.alertsId = null
      return null
    })
    alerts = (row && Number(row.UnreadCount)) || 0
  }
  const mentions = alerts > 0 ? 0 : (((await api.mentions()) || {}).Items || []).length
  return { alerts, mentions }
}

// The badge counts, shared between the ARTEMIS tab in the top menu and whichever Artemis panel last
// loaded the list. subscribe(fn) → unsubscribe.
const listeners = new Set()
let latest = null
export const badgeStore = {
  get: () => latest,
  set: badges => {
    if (latest && badges && latest.alerts === badges.alerts && latest.mentions === badges.mentions) return
    latest = badges
    listeners.forEach(fn => fn(latest))
  },
  subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn) }
}

// A section another surface asked the hub to open on (the map panel's View all): read once.
let requested = null
export const requestSection = section => { requested = SECTIONS.includes(section) ? section : null }
export const takeRequestedSection = () => { const s = requested; requested = null; return s }
